import { apiClient } from "./client";
import { Did, PaginatedResponse } from "@/types";

export interface ClientAddress {
  street?: string;
  zipcode?: string;
  city?: string;
  country?: string;
}

export interface Client {
  id: Did;
  name: string;
  email?: string;
  phone?: string;
  active: boolean;
  address_json?: ClientAddress;
}

export interface Project {
  id: Did;
  client_id: Did;
  client?: Client;
  code: string;
  name: string;
  display_name?: string;
  address_json?: ClientAddress;
  status: "open" | "closed" | "completed";
  price_cents_incl: number;
  price_cents_excl: number;
  manual_invoice_amount_cents?: number | null;
  budget_hours?: number;
  start_date?: string;
  end_date?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateProjectPayload {
  client_id: Did;
  address_json?: ClientAddress;
  name?: string;
  code?: string;
  price_cents_incl?: number;
  budget_hours?: number;
  description?: string;
  start_date?: string;
}

export interface ProjectWithStats extends Project {
  total_hours: number;
  photos_count: number;
  last_activity?: string;
}

/**
 * Get all projects for the authenticated user
 */
export const getProjects = async (): Promise<Project[]> => {
  const { data } = await apiClient.get<{ data: Project[] }>("/projects");
  return data.data;
};

/**
 * Get a single project with details
 */
export const getProject = async (id: Did): Promise<ProjectWithStats> => {
  const { data } = await apiClient.get<ProjectWithStats>(`/projects/${id}`);
  return data;
};

export interface ProjectSearchParams {
  search?: string;
  status?: "open" | "closed";
  page?: number;
}

/**
 * Get active projects only
 */
export const getActiveProjects = async (): Promise<Project[]> => {
  const { data } = await apiClient.get<{ data: Project[] }>("/projects", {
    params: { status: "open" },
  });
  return data.data;
};

/**
 * Get paginated projects with search
 */
export const getProjectsPaginated = async (
  params: ProjectSearchParams = {}
): Promise<PaginatedResponse<Project>> => {
  const { data } = await apiClient.get<PaginatedResponse<Project>>(
    "/projects",
    {
      params: {
        status: "open",
        mobile_selectable: 1,
        ...params,
      },
    }
  );
  return data;
};

/**
 * Create a new project from the mobile app.
 */
export const createProject = async (
  payload: CreateProjectPayload
): Promise<Project> => {
  const { data } = await apiClient.post<Project>("/projects", payload);
  return data;
};

export interface CreateClientPayload {
  name: string;
  email?: string;
  phone?: string;
  address_json?: ClientAddress;
}

/**
 * Create a new client from the mobile app.
 */
export const createClient = async (
  payload: CreateClientPayload
): Promise<Client> => {
  const { data } = await apiClient.post<Client>("/clients", payload);
  return data;
};

/**
 * Mark a project as completed.
 */
export const completeProject = async (id: Did): Promise<Project> => {
  const { data } = await apiClient.post<Project>(`/projects/${id}/complete`);
  return data;
};

/**
 * Re-open a completed project.
 */
export const reopenProject = async (id: Did): Promise<Project> => {
  const { data } = await apiClient.post<Project>(`/projects/${id}/reopen`);
  return data;
};
